import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Button,
    TextField,
    Alert,
    CircularProgress,
    Grid,
    Paper,
    Tabs,
    Tab,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Tooltip,
    IconButton,
    Divider,
    Stack,
    Chip,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import {
    Save as SaveIcon,
    RestartAlt as ResetIcon,
    Upload as UploadIcon,
    Delete as DeleteImageIcon,
    Visibility as PreviewIcon,
    Article as ClientReportIcon,
    Restaurant as AlimIcon,
    FormatBold,
    FormatItalic,
    FormatUnderlined,
    FormatColorText,
    FormatAlignLeft,
    FormatAlignCenter,
    FormatAlignRight,
    FormatClear,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import FontFamily from '@tiptap/extension-font-family';
import TextAlign from '@tiptap/extension-text-align';
import { Extension } from '@tiptap/core';
import { PageHeader } from '../../components/common/PageHeader';
import { reportFormatService } from '../../services/reportFormatService';
import {
    ReportFormat,
    ReportFormatType,
    REPORT_FORMAT_DEFAULTS,
    COLOR_PRESETS,
} from '../../types/reportFormat';

// ── Inline FontSize TipTap extension (no external package needed) ─────────────

const FontSize = Extension.create({
    name: 'fontSize',
    addOptions() { return { types: ['textStyle'] }; },
    addGlobalAttributes() {
        return [{
            types: this.options.types,
            attributes: {
                fontSize: {
                    default: null,
                    parseHTML: el => el.style.fontSize?.replace(/['"]+/g, '') || null,
                    renderHTML: attrs => attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {},
                },
            },
        }];
    },
    addCommands() {
        return {
            setFontSize: (size: string) => ({ chain }: any) =>
                chain().setMark('textStyle', { fontSize: size }).run(),
            unsetFontSize: () => ({ chain }: any) =>
                chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
        } as any;
    },
});

// ── Constants ─────────────────────────────────────────────────────────────────

const EMPTY_FORMAT: ReportFormat = {
    report_type: 'client_report',
    header_text: '',
    header_image: null,
    footer_text: '',
    accent_color: null,
    font_family: null,
    header_font_size: null,
    header_text_color: null,
    custom_intro: '',
    custom_closing: '',
};

const TABS: { type: ReportFormatType; labelKey: string; icon: JSX.Element }[] = [
    { type: 'client_report', labelKey: 'reportFormatting.tab.clientReport', icon: <ClientReportIcon /> },
    { type: 'alimentation_report', labelKey: 'reportFormatting.tab.alimentationReport', icon: <AlimIcon /> },
];

const FONT_FAMILIES = ['Arial', 'Georgia', 'Times New Roman', 'Helvetica', 'Segoe UI'];
const FONT_SIZES = ['8pt', '9pt', '10pt', '11pt', '12pt', '14pt', '16pt', '18pt', '20pt', '24pt'];

// ── Rich text toolbar ─────────────────────────────────────────────────────────

function RichTextToolbar({ editor }: { editor: ReturnType<typeof useEditor> }) {
    if (!editor) return null;

    const colorInputRef = useRef<HTMLInputElement>(null);

    const currentFontFamily = editor.getAttributes('textStyle').fontFamily || '';
    const currentFontSize = editor.getAttributes('textStyle').fontSize || '';
    const currentColor = editor.getAttributes('textStyle').color || '#000000';

    const currentAlign = editor.isActive({ textAlign: 'center' })
        ? 'center'
        : editor.isActive({ textAlign: 'right' })
            ? 'right'
            : 'left';

    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.75,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50',
                borderRadius: '4px 4px 0 0',
            }}
        >
            {/* Font family */}
            <Select
                size="small"
                value={currentFontFamily}
                onChange={(e) => {
                    if (e.target.value) editor.chain().focus().setFontFamily(e.target.value).run();
                    else editor.chain().focus().unsetFontFamily().run();
                }}
                displayEmpty
                sx={{ fontSize: 12, minWidth: 110, height: 28, '& .MuiSelect-select': { py: 0.25 } }}
            >
                <MenuItem value=""><em style={{ fontSize: 12 }}>Police</em></MenuItem>
                {FONT_FAMILIES.map(f => (
                    <MenuItem key={f} value={f} sx={{ fontFamily: f, fontSize: 13 }}>{f}</MenuItem>
                ))}
            </Select>

            {/* Font size */}
            <Select
                size="small"
                value={currentFontSize}
                onChange={(e) => {
                    if (e.target.value) (editor.chain().focus() as any).setFontSize(e.target.value).run();
                    else (editor.chain().focus() as any).unsetFontSize().run();
                }}
                displayEmpty
                sx={{ fontSize: 12, minWidth: 72, height: 28, '& .MuiSelect-select': { py: 0.25 } }}
            >
                <MenuItem value=""><em style={{ fontSize: 12 }}>Taille</em></MenuItem>
                {FONT_SIZES.map(s => (
                    <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>{s}</MenuItem>
                ))}
            </Select>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

            {/* Bold */}
            <Tooltip title="Gras (Ctrl+B)">
                <IconButton
                    size="small"
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    color={editor.isActive('bold') ? 'primary' : 'default'}
                    sx={{ p: 0.5 }}
                >
                    <FormatBold fontSize="small" />
                </IconButton>
            </Tooltip>

            {/* Italic */}
            <Tooltip title="Italique (Ctrl+I)">
                <IconButton
                    size="small"
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    color={editor.isActive('italic') ? 'primary' : 'default'}
                    sx={{ p: 0.5 }}
                >
                    <FormatItalic fontSize="small" />
                </IconButton>
            </Tooltip>

            {/* Underline */}
            <Tooltip title="Souligné (Ctrl+U)">
                <IconButton
                    size="small"
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    color={editor.isActive('underline') ? 'primary' : 'default'}
                    sx={{ p: 0.5 }}
                >
                    <FormatUnderlined fontSize="small" />
                </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

            {/* Text color */}
            <Tooltip title="Couleur du texte">
                <Box
                    sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                    onClick={() => colorInputRef.current?.click()}
                >
                    <FormatColorText fontSize="small" sx={{ color: currentColor }} />
                    <Box sx={{ width: 16, height: 3, bgcolor: currentColor, mt: 0.25, borderRadius: 0.5 }} />
                </Box>
            </Tooltip>
            <input
                ref={colorInputRef}
                type="color"
                value={currentColor}
                style={{ display: 'none' }}
                onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
            />

            <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

            {/* Alignment */}
            <ToggleButtonGroup
                size="small"
                value={currentAlign}
                exclusive
                onChange={(_, val) => {
                    if (val) editor.chain().focus().setTextAlign(val).run();
                }}
                sx={{ height: 28 }}
            >
                <ToggleButton value="left" sx={{ p: 0.5, border: 'none' }}>
                    <FormatAlignLeft fontSize="small" />
                </ToggleButton>
                <ToggleButton value="center" sx={{ p: 0.5, border: 'none' }}>
                    <FormatAlignCenter fontSize="small" />
                </ToggleButton>
                <ToggleButton value="right" sx={{ p: 0.5, border: 'none' }}>
                    <FormatAlignRight fontSize="small" />
                </ToggleButton>
            </ToggleButtonGroup>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

            {/* Clear formatting */}
            <Tooltip title="Effacer la mise en forme">
                <IconButton
                    size="small"
                    onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
                    sx={{ p: 0.5 }}
                >
                    <FormatClear fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );
}

// ── Live print preview ────────────────────────────────────────────────────────

function buildPreviewHtml(fmt: Partial<ReportFormat>, type: ReportFormatType): string {
    const defaults = REPORT_FORMAT_DEFAULTS[type];
    const accent = fmt.accent_color || defaults.accent_color || '#1565c0';
    const font = fmt.font_family || defaults.font_family || 'Arial';
    const headerHtml = fmt.header_text || '';
    const footerText = fmt.footer_text || '';
    const customIntro = fmt.custom_intro || '';
    const customClosing = fmt.custom_closing || '';
    const hasImage = !!fmt.header_image;

    const sampleBody = type === 'client_report'
        ? `<h3>Compte rendu de consultation</h3>
           <p>Bonjour,</p>
           ${customIntro ? `<p><em>${customIntro}</em></p>` : ''}
           <p>Suite à la consultation de votre animal, voici le résumé de la visite :</p>
           <ul>
             <li><strong>Examen clinique :</strong> Bon état général.</li>
             <li><strong>Diagnostic :</strong> Otite externe légère.</li>
             <li><strong>Traitement :</strong> Nettoyage auriculaire quotidien pendant 7 jours.</li>
           </ul>
           ${customClosing ? `<p>${customClosing}</p>` : '<p>Cordialement,<br/>Dr. Exemple</p>'}`
        : `<h3>Guide nutritionnel — Rex</h3>
           <p><strong>Date :</strong> ${new Date().toLocaleDateString('fr-FR')}</p>
           ${customIntro ? `<p><em>${customIntro}</em></p>` : ''}
           <h4>Besoin énergétique</h4>
           <p>Besoin calculé : <strong>1 245 kcal/jour</strong></p>
           <h4>Ration journalière recommandée</h4>
           <p>Royal Canin Medium Adult — 250 g/jour</p>
           ${customClosing ? `<p>${customClosing}</p>` : '<p>Ces recommandations sont données à titre indicatif.</p>'}`;

    const headerSection = (hasImage || headerHtml)
        ? `<div class="doc-header">
               ${hasImage ? `<img src="${fmt.header_image}" class="header-logo" alt="logo" />` : ''}
               ${headerHtml ? `<div class="header-rich">${headerHtml}</div>` : ''}
           </div>`
        : `<div class="doc-header placeholder">En-tête de votre cabinet</div>`;

    return `<!DOCTYPE html><html><head><style>
        body { font-family: ${font}, sans-serif; font-size: 11px; color: #333; margin: 0; padding: 12px 16px; line-height: 1.5; }
        .doc-header { display: flex; align-items: flex-start; gap: 12px; padding-bottom: 10px; border-bottom: 2px solid ${accent}; margin-bottom: 14px; }
        .doc-header.placeholder { color: #bbb; font-style: italic; font-size: 10px; }
        .header-logo { max-height: 60px; max-width: 120px; object-fit: contain; flex-shrink: 0; }
        .header-rich { font-size: 10px; flex: 1; min-width: 0; }
        .header-rich p { margin: 0; }
        h3 { color: ${accent}; border-bottom: 1px solid ${accent}; padding-bottom: 4px; font-size: 13px; margin: 0 0 10px; }
        h4 { color: ${accent}; font-size: 11px; margin: 10px 0 4px; }
        ul { padding-left: 16px; margin: 4px 0; }
        li { margin-bottom: 2px; }
        .footer { margin-top: 16px; padding-top: 8px; border-top: 1px solid #ddd; font-size: 9px; color: #888; white-space: pre-line; }
    </style></head><body>
        ${headerSection}
        ${sampleBody}
        ${footerText ? `<div class="footer">${footerText.replace(/\n/g, '<br/>')}</div>` : ''}
    </body></html>`;
}

// ── Format editor tab panel ───────────────────────────────────────────────────

function FormatEditor({ reportType }: { reportType: ReportFormatType }) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [form, setForm] = useState<Partial<ReportFormat>>({});
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const defaults = REPORT_FORMAT_DEFAULTS[reportType];

    const { data: savedFormat, isLoading } = useQuery({
        queryKey: ['reportFormat', reportType],
        queryFn: () => reportFormatService.getFormat(reportType),
        retry: false,
        throwOnError: false,
    });

    // TipTap editor for the header rich text
    const editor = useEditor({
        extensions: [
            StarterKit.configure({ heading: false }),
            Underline,
            TextStyle,
            FontFamily,
            FontSize,
            Color,
            TextAlign.configure({ types: ['paragraph'] }),
        ],
        content: '',
        onUpdate: ({ editor }) => {
            setForm(prev => ({ ...prev, header_text: editor.getHTML() }));
        },
    });

    useEffect(() => {
        if (!editor) return;
        const html = savedFormat?.header_text ?? '';
        if (editor.getHTML() !== html) editor.commands.setContent(html);
    }, [savedFormat, editor]);

    useEffect(() => {
        if (savedFormat) {
            setForm({
                header_text: savedFormat.header_text ?? '',
                header_image: savedFormat.header_image ?? null,
                footer_text: savedFormat.footer_text ?? '',
                accent_color: savedFormat.accent_color ?? defaults.accent_color ?? '',
                font_family: savedFormat.font_family ?? defaults.font_family ?? 'Arial',
                header_font_size: null,
                header_text_color: null,
                custom_intro: savedFormat.custom_intro ?? '',
                custom_closing: savedFormat.custom_closing ?? '',
            });
        } else if (!isLoading) {
            setForm({
                header_text: '',
                header_image: null,
                footer_text: '',
                accent_color: defaults.accent_color ?? '',
                font_family: defaults.font_family ?? 'Arial',
                header_font_size: null,
                header_text_color: null,
                custom_intro: '',
                custom_closing: '',
            });
        }
    }, [savedFormat, isLoading]);

    const saveMutation = useMutation({
        mutationFn: () => reportFormatService.updateFormat(reportType, form),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['reportFormat', reportType] });
            setSuccess(true);
            setError(null);
            setTimeout(() => setSuccess(false), 3000);
        },
        onError: () => setError(t('reportFormatting.saveError')),
    });

    const resetMutation = useMutation({
        mutationFn: () => reportFormatService.resetFormat(reportType),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['reportFormat', reportType] });
            editor?.commands.setContent('');
            setForm({
                header_text: '',
                header_image: null,
                footer_text: '',
                accent_color: defaults.accent_color ?? '',
                font_family: defaults.font_family ?? 'Arial',
                header_font_size: null,
                header_text_color: null,
                custom_intro: '',
                custom_closing: '',
            });
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        },
        onError: () => setError(t('reportFormatting.saveError')),
    });

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            setForm((prev) => ({ ...prev, header_image: ev.target?.result as string }));
            if (file.size > 3 * 1024 * 1024) setError(t('reportFormatting.imageLargeWarning'));
        };
        reader.readAsDataURL(file);
    };

    const handlePreview = () => {
        const html = buildPreviewHtml(form, reportType);
        const win = window.open('', '_blank');
        if (!win) return;
        win.document.write(html);
        win.document.close();
    };

    if (isLoading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
    }

    const accentValue = form.accent_color || defaults.accent_color || '#1565c0';
    const fontValue = form.font_family || defaults.font_family || 'Arial';

    return (
        <Grid container spacing={3}>
            {/* ── Left column: form ── */}
            <Grid item xs={12} md={7}>
                <Stack spacing={3}>
                    {/* Header section */}
                    <Paper variant="outlined" sx={{ p: 2.5 }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                            {t('reportFormatting.section.header')}
                        </Typography>

                        {/* Logo upload */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            {form.header_image ? (
                                <>
                                    <Box
                                        component="img"
                                        src={form.header_image}
                                        alt="logo"
                                        sx={{ height: 48, maxWidth: 140, objectFit: 'contain', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.5 }}
                                    />
                                    <Tooltip title={t('reportFormatting.removeLogo')}>
                                        <IconButton size="small" color="error" onClick={() => setForm((prev) => ({ ...prev, header_image: null }))}>
                                            <DeleteImageIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            ) : (
                                <Button variant="outlined" size="small" startIcon={<UploadIcon />} onClick={() => fileInputRef.current?.click()}>
                                    {t('reportFormatting.uploadLogo')}
                                </Button>
                            )}
                            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
                            <Typography variant="caption" color="text.secondary">{t('reportFormatting.logoHint')}</Typography>
                        </Box>

                        {/* Rich text editor */}
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            {t('reportFormatting.headerText')}
                        </Typography>
                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                '&:focus-within': { borderColor: 'primary.main', boxShadow: '0 0 0 2px rgba(25,118,210,0.1)' },
                                '& .ProseMirror': {
                                    outline: 'none',
                                    minHeight: 80,
                                    px: 1.5,
                                    py: 1,
                                    fontSize: 13,
                                    '& p': { margin: '0 0 2px' },
                                },
                            }}
                        >
                            <RichTextToolbar editor={editor} />
                            <EditorContent editor={editor} />
                        </Box>
                    </Paper>

                    {/* Footer section */}
                    <Paper variant="outlined" sx={{ p: 2.5 }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                            {t('reportFormatting.section.footer')}
                        </Typography>
                        <TextField
                            fullWidth multiline rows={3} size="small"
                            label={t('reportFormatting.footerText')}
                            placeholder={t('reportFormatting.footerTextPlaceholder')}
                            value={form.footer_text ?? ''}
                            onChange={(e) => setForm((prev) => ({ ...prev, footer_text: e.target.value }))}
                        />
                    </Paper>

                    {/* Styling section */}
                    <Paper variant="outlined" sx={{ p: 2.5 }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                            {t('reportFormatting.section.style')}
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>{t('reportFormatting.fontFamily')}</InputLabel>
                                    <Select
                                        value={fontValue}
                                        label={t('reportFormatting.fontFamily')}
                                        onChange={(e) => setForm((prev) => ({ ...prev, font_family: e.target.value }))}
                                    >
                                        {['Arial', 'Helvetica', 'Georgia', 'Times New Roman', "'Segoe UI', sans-serif"].map((f) => (
                                            <MenuItem key={f} value={f} sx={{ fontFamily: f }}>{f.replace(/['"]/g, '')}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                        {t('reportFormatting.accentColor')}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <Box
                                            component="input" type="color" value={accentValue}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                setForm((prev) => ({ ...prev, accent_color: e.target.value }))
                                            }
                                            sx={{ width: 36, height: 36, border: '1px solid', borderColor: 'divider', borderRadius: 1, cursor: 'pointer', padding: '2px' }}
                                        />
                                        {COLOR_PRESETS.map((c) => (
                                            <Box key={c} onClick={() => setForm((prev) => ({ ...prev, accent_color: c }))}
                                                sx={{
                                                    width: 22, height: 22, bgcolor: c, borderRadius: '50%', cursor: 'pointer',
                                                    border: accentValue === c ? '2px solid #333' : '2px solid transparent',
                                                    transition: 'transform 0.1s', '&:hover': { transform: 'scale(1.2)' },
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Content section */}
                    <Paper variant="outlined" sx={{ p: 2.5 }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                            {t('reportFormatting.section.content')}
                        </Typography>
                        <Stack spacing={2}>
                            <TextField
                                fullWidth multiline rows={2} size="small"
                                label={t('reportFormatting.customIntro')}
                                placeholder={t('reportFormatting.customIntroPlaceholder')}
                                value={form.custom_intro ?? ''}
                                onChange={(e) => setForm((prev) => ({ ...prev, custom_intro: e.target.value }))}
                            />
                            <TextField
                                fullWidth multiline rows={2} size="small"
                                label={t('reportFormatting.customClosing')}
                                placeholder={t('reportFormatting.customClosingPlaceholder')}
                                value={form.custom_closing ?? ''}
                                onChange={(e) => setForm((prev) => ({ ...prev, custom_closing: e.target.value }))}
                            />
                        </Stack>
                    </Paper>

                    {success && <Alert severity="success">{t('reportFormatting.saveSuccess')}</Alert>}
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="contained"
                            startIcon={saveMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                            onClick={() => saveMutation.mutate()}
                            disabled={saveMutation.isPending}
                        >
                            {t('common.save')}
                        </Button>
                        <Button variant="outlined" startIcon={<PreviewIcon />} onClick={handlePreview}>
                            {t('reportFormatting.preview')}
                        </Button>
                        <Button variant="outlined" color="warning" startIcon={<ResetIcon />}
                            onClick={() => resetMutation.mutate()} disabled={resetMutation.isPending}>
                            {t('reportFormatting.reset')}
                        </Button>
                    </Box>
                </Stack>
            </Grid>

            {/* ── Right column: live preview ── */}
            <Grid item xs={12} md={5}>
                <Paper variant="outlined" sx={{ p: 2, position: 'sticky', top: 24 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <PreviewIcon fontSize="small" color="action" />
                        <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                            {t('reportFormatting.livePreview')}
                        </Typography>
                        <Chip label={t('reportFormatting.sample')} size="small" variant="outlined" sx={{ ml: 'auto' }} />
                    </Box>
                    <Divider sx={{ mb: 1.5 }} />
                    <Box
                        component="iframe"
                        srcDoc={buildPreviewHtml(form, reportType)}
                        sx={{
                            width: '100%', height: 420, border: '1px solid', borderColor: 'divider',
                            borderRadius: 1, bgcolor: 'white',
                        }}
                        title="preview"
                    />
                </Paper>
            </Grid>
        </Grid>
    );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export const ReportFormattingPage = () => {
    const { t } = useTranslation();
    const [tab, setTab] = useState(0);

    return (
        <Box>
            <PageHeader title={t('reportFormatting.title')} subtitle={t('reportFormatting.subtitle')} />
            <Paper sx={{ mb: 3 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    {TABS.map((t) => (
                        <Tab key={t.type} icon={t.icon} iconPosition="start" label={useTranslation().t(t.labelKey)} />
                    ))}
                </Tabs>
            </Paper>
            {TABS.map((t, i) => (
                <Box key={t.type} hidden={tab !== i} sx={{ mt: 1 }}>
                    {tab === i && <FormatEditor reportType={t.type} />}
                </Box>
            ))}
        </Box>
    );
};
